import { Flex, Spin, Text } from '@byecode/ui'
import type { ActorEvent, BackgroundNoImageProtocol } from '@lighthouse/core'
import { DataSourceType, type Pagination, type RecordLikeProtocol } from '@lighthouse/core'
import { type FlowLayoutNode, getPrimaryDsSystemFieldId, getRecordPoolKey, useAtomData } from '@lighthouse/shared'
import equal from 'fast-deep-equal'
import React, { forwardRef, memo, useCallback, useDeferredValue, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { useViewBlockContext } from '../../context'
import { ReplayItem } from './ReplayItem'

// 哨兵元素，用于触发懒加载
const LoadingSentinel = styled.div`
    height: 20px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`

// 加载状态指示器
const LoadingIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    gap: 8px;
`

interface Props {
    id?: string
    node: FlowLayoutNode
    title: string
    gap: number
    readonly?: boolean
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onLoadMoreData: (pageNum: number) => Promise<RecordLikeProtocol[]>
    pagination: Pagination

    replayCountMap: Record<string, number>
    onLoadReplayData: (payload: { pageNum: number; pageSize: number; rootId: string }) => Promise<void>

    parentIdKey: string
    rootIdKey: string
    childIndent?: number
    replayTextColor?: BackgroundNoImageProtocol
    replayIconColor?: BackgroundNoImageProtocol
}

type TreeRecords = {
    data: RecordLikeProtocol
    total: number
    children?: RecordLikeProtocol[]
}

export const CommentView = memo(
    forwardRef<HTMLDivElement, Props>((props, ref) => {
        const {
            id: elementId,
            node,
            title,
            gap,
            readonly,
            onRecordClick,
            onLoadMoreData,
            pagination,

            replayCountMap,
            onLoadReplayData,

            parentIdKey,
            rootIdKey,
            childIndent,
            replayIconColor,
            replayTextColor
        } = props
        const { blockData, recordPoints, recordPoolAtom, dataSource, dataSourceList } = useViewBlockContext()
        const { id: dsId, schema, viewOptions, type: dsType } = dataSource
        const { id } = blockData

        // 懒加载状态管理
        const [loadingState, setLoadingState] = useImmer({ loading: false, hasMore: true })
        const loadingRef = useRef(false)
        const sentinelRef = useRef<HTMLDivElement>(null)

        // 分页信息
        const { currentPage = 1, pageSize = 10, rowTotal = 0 } = pagination

        // 计算是否还有更多数据
        const hasMoreData = rowTotal > currentPage * pageSize

        // 加载更多主评论的处理函数
        const handleLoadMoreComments = useCallback(async () => {
            if (loadingRef.current || !hasMoreData) {
                return
            }

            const cleanup = () => {
                loadingRef.current = false
                setLoadingState(draft => {
                    draft.loading = false
                })
            }

            loadingRef.current = true
            setLoadingState(draft => {
                draft.loading = true
            })

            try {
                await onLoadMoreData(currentPage + 1)
            } finally {
                cleanup()
            }
        }, [currentPage, hasMoreData, onLoadMoreData, setLoadingState])

        // 使用 Intersection Observer 监听哨兵元素
        useEffect(() => {
            const sentinel = sentinelRef.current
            if (!sentinel || !hasMoreData) {
                return
            }

            const observer = new IntersectionObserver(
                entries => {
                    const [entry] = entries
                    // 当哨兵元素进入视口且有更多数据时，触发加载
                    if (entry.isIntersecting && hasMoreData && !loadingRef.current) {
                        handleLoadMoreComments()
                    }
                },
                {
                    // 提前触发：当哨兵元素距离视口底部 50px 时就开始加载
                    rootMargin: '0px 0px 50px 0px',
                    threshold: 0.1
                }
            )

            observer.observe(sentinel)

            return () => {
                observer.unobserve(sentinel)
                observer.disconnect()
            }
        }, [hasMoreData, handleLoadMoreComments])

        const idFieldId = useMemo(
            () => (dsType === DataSourceType.joinDataSource ? getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType) : 'ID') ?? 'ID',
            [dsType, schema, viewOptions]
        )

        // 组装树形结构来渲染
        const replayTree = useDeferredValue(
            useAtomData(
                recordPoolAtom,
                useCallback(
                    pool => {
                        const records = recordPoints
                            .map(p => pool.get(getRecordPoolKey(dsId, p.id)))
                            .filter((r): r is RecordLikeProtocol => r !== undefined)

                        const tree: TreeRecords[] = []

                        // 找到所有根节点
                        for (const record of records) {
                            const replayRootValue = record.content[rootIdKey]?.value
                            const replayParentValue = record.content[parentIdKey]?.value

                            // 如果没有父级及根级回复对象，则是根级回复
                            if (replayRootValue === undefined && replayParentValue === undefined) {
                                tree.push({ data: record, total: replayCountMap[record.id] || 0 })
                            }
                        }

                        // 插入子级评论
                        for (const record of records) {
                            const replayRootValue = record.content[rootIdKey]?.value
                            if (replayRootValue === undefined) {
                                continue
                            }

                            const treeTarget = tree.find(item => equal(item.data.content[idFieldId]?.value, replayRootValue))
                            if (treeTarget) {
                                if (!treeTarget.children) {
                                    treeTarget.children = []
                                }

                                treeTarget.children.push(record)
                            }
                        }

                        return tree
                    },
                    [dsId, idFieldId, parentIdKey, recordPoints, replayCountMap, rootIdKey]
                )
            )
        )

        return (
            <Flex ref={ref} id={elementId} direction="column" gap={gap}>
                {replayTree.map((treeRecord, index) => {
                    const { data: record, total, children } = treeRecord

                    return (
                        <ReplayItem
                            key={record.id}
                            index={index}
                            record={record}
                            idFieldId={idFieldId}
                            total={total}
                            childRecords={children}
                            node={node}
                            title={title}
                            readonly={readonly}
                            gap={gap}
                            childIndent={childIndent}
                            replayIconColor={replayIconColor}
                            replayTextColor={replayTextColor}
                            onRecordClick={onRecordClick}
                            onLoadReplayData={onLoadReplayData}
                        />
                    )
                })}

                {/* 加载状态指示器 */}
                {loadingState.loading && (
                    <LoadingIndicator>
                        <Spin size={16} />
                        <Text size={14} color="var(--color-text-secondary)">
                            正在加载更多评论...
                        </Text>
                    </LoadingIndicator>
                )}

                {/* 哨兵元素，用于触发懒加载 */}
                {hasMoreData && <LoadingSentinel ref={sentinelRef} />}
            </Flex>
        )
    })
)
